import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, BellRing, Loader2, Play, RefreshCw, Camera, FileSignature,
  ShieldCheck, Banknote, Users, Send,
} from 'lucide-react';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';

const ITEM_META = {
  profile_photo: { label: 'Profile photo', icon: Camera, key: 'missing_profile_photo' },
  signed_contract: { label: 'Signed agreement', icon: FileSignature, key: 'missing_signed_contract' },
  id_verification: { label: 'ID verification', icon: ShieldCheck, key: 'missing_id_verification' },
  stripe_payout: { label: 'Payout setup', icon: Banknote, key: 'missing_stripe_payout' },
};

const STATUS_STYLE = {
  sent: 'bg-green-600',
  blocked: 'text-amber-700 border-amber-300',
  skipped: 'text-amber-700 border-amber-300',
  failed: 'bg-red-500',
};

export default function ProfileNudges() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [freqDraft, setFreqDraft] = useState('7');

  const loadAll = useCallback(async () => {
    try {
      const [s, p, h] = await Promise.all([
        api.get('/admin/profile-nudges/settings'),
        api.get('/admin/profile-nudges/preview'),
        api.get('/admin/profile-nudges/history?limit=50'),
      ]);
      setSettings(s.data);
      setFreqDraft(String(s.data?.frequency_days ?? 7));
      setPreview(p.data);
      setHistory(h.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Smart Reminders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveSettings = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/profile-nudges/settings', patch);
      setSettings(data);
      setFreqDraft(String(data.frequency_days));
      toast.success('Settings saved');
      api.get('/admin/profile-nudges/preview').then(r => setPreview(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post('/admin/profile-nudges/run-now');
      toast.success('Sweep started — results will appear in a moment');
      setTimeout(() => {
        api.get('/admin/profile-nudges/history?limit=50').then(r => setHistory(r.data)).catch(() => {});
        api.get('/admin/profile-nudges/preview').then(r => setPreview(r.data)).catch(() => {});
      }, 6000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start sweep');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
      </div>
    );
  }

  const enabled = !!settings?.enabled;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-nudges-page">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/talent-communications')}
          data-testid="pn-back" className="mb-3">
          <ArrowLeft className="w-4 h-4 mr-1" /> Talent Communications
        </Button>

        <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2">
          <BellRing className="w-6 h-6 text-cyan-600" /> Smart Reminder Emails
        </h1>
        <p className="text-sm text-slate-500 mb-5">
          Automatically nudge active talent to finish their profile — adding a photo, signing the
          contractor agreement, verifying ID and setting up payouts. Suspended, unsubscribed and
          opted-out talent are always excluded.
        </p>

        {/* Master control */}
        <Card className="mb-4">
          <CardHeader><CardTitle className="text-base">Daily reminders</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-slate-800">Send profile-completion reminders</div>
                <div className="text-xs text-slate-500">
                  When on, the daily sweep emails talent with an incomplete profile.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={enabled ? 'bg-green-600' : 'bg-slate-400'} data-testid="pn-status-badge">
                  {enabled ? 'On' : 'Off'}
                </Badge>
                <Switch checked={enabled} disabled={saving}
                  onCheckedChange={(v) => saveSettings({ enabled: v })}
                  data-testid="pn-enable-toggle" />
              </div>
            </div>

            <div className="flex items-end gap-2 pt-2 border-t">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Minimum days between reminders</label>
                <Input type="number" min={1} value={freqDraft} onChange={e => setFreqDraft(e.target.value)}
                  className="w-28" data-testid="pn-frequency-input" />
              </div>
              <Button variant="outline" disabled={saving || String(settings?.frequency_days) === freqDraft}
                onClick={() => saveSettings({ frequency_days: parseInt(freqDraft, 10) || 7 })}
                data-testid="pn-save-frequency">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
              <div className="flex-1" />
              <Button onClick={runNow} disabled={running} className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="pn-run-now">
                {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                Run now
              </Button>
            </div>
            <p className="text-[11px] text-slate-400">
              “Run now” sends immediately even while the daily switch is off, so you can test it. The
              frequency cap and exclusions still apply.
            </p>
          </CardContent>
        </Card>

        {/* Who is incomplete */}
        <Card className="mb-4">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-cyan-600" /> Who needs a nudge</CardTitle>
            <Button variant="ghost" size="sm" onClick={loadAll} data-testid="pn-refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg border bg-white p-3" data-testid="pn-stat-active">
                <div className="text-2xl font-semibold text-slate-800">{(preview?.total_active ?? 0).toLocaleString()}</div>
                <div className="text-xs text-slate-500">Active talent</div>
              </div>
              <div className="rounded-lg border bg-white p-3" data-testid="pn-stat-incomplete">
                <div className="text-2xl font-semibold text-cyan-700">{(preview?.any_incomplete ?? 0).toLocaleString()}</div>
                <div className="text-xs text-slate-500">Incomplete profiles</div>
              </div>
              <div className="rounded-lg border bg-white p-3" data-testid="pn-stat-nudged">
                <div className="text-2xl font-semibold text-slate-800">{(preview?.nudged_within_window ?? 0).toLocaleString()}</div>
                <div className="text-xs text-slate-500">Nudged in last {preview?.frequency_days ?? 7}d</div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {Object.entries(ITEM_META).map(([k, meta]) => {
                const Icon = meta.icon;
                return (
                  <div key={k} className="flex items-center justify-between rounded-md border bg-slate-50 px-3 py-2"
                    data-testid={`pn-missing-${k}`}>
                    <span className="flex items-center gap-2 text-sm text-slate-700">
                      <Icon className="w-4 h-4 text-slate-500" /> {meta.label} missing
                    </span>
                    <Badge variant="secondary">{(preview?.[meta.key] ?? 0).toLocaleString()}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Delivery history */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Send className="w-4 h-4 text-cyan-600" /> Recent deliveries</CardTitle>
          </CardHeader>
          <CardContent>
            {history?.stats_30d && Object.keys(history.stats_30d).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="pn-stats-30d">
                {Object.entries(history.stats_30d).map(([st, n]) => (
                  <Badge key={st} variant="outline" className="text-slate-600">{st}: {n}</Badge>
                ))}
              </div>
            )}
            {(!history?.items || history.items.length === 0) ? (
              <p className="text-sm text-slate-400">No reminders sent yet.</p>
            ) : (
              <div className="divide-y" data-testid="pn-history">
                {history.items.map(row => (
                  <div key={row.id} className="py-2 flex items-center justify-between gap-3" data-testid={`pn-history-${row.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{row.name || row.email}</div>
                      <div className="text-xs text-slate-400 truncate">
                        {(row.missing_items || '').split(',').filter(Boolean).map(k => ITEM_META[k]?.label || k).join(', ')}
                        {row.created_at ? ` · ${formatDateMMDDYYYY(row.created_at)}` : ''}
                        {row.skip_reason ? ` · ${row.skip_reason}` : ''}
                      </div>
                    </div>
                    <Badge className={typeof STATUS_STYLE[row.status] === 'string' && STATUS_STYLE[row.status].startsWith('bg-') ? STATUS_STYLE[row.status] : ''}
                      variant={STATUS_STYLE[row.status]?.startsWith('bg-') ? undefined : 'outline'}>
                      {row.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
